// MapRoute.js
import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';

mapboxgl.accessToken = 'pk.eyJ1IjoiZGRzbmFja3MiLCJhIjoiY21jbHFtbmNiMDk2dTJxczlpMnIxbWNrcCJ9.QEX0ufFo7NoOSeN6EzhLXw';

const MapRoute = () => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [routeInput, setRouteInput] = useState('GU34 5LD, HA2 6FA');
  const [useCurrentLocation, setUseCurrentLocation] = useState(false);
  const [infoText, setInfoText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const markersRef = useRef([]);

  const getCoordinates = async (postcode) => {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
        postcode
      )}.json?country=GB&limit=1&access_token=${mapboxgl.accessToken}`
    );
    const data = await response.json();
    if (!data.features.length) throw new Error(`No location for ${postcode}`);
    return data.features[0].center;
  };

  const plotRoute = async () => {
    setInfoText('');
    setIsLoading(true);
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    if (mapRef.current.getLayer('route')) mapRef.current.removeLayer('route');
    if (mapRef.current.getSource('route')) mapRef.current.removeSource('route');

    const postcodes = routeInput.split(',').map((p) => p.trim()).filter(Boolean);
    const coords = [];

    try {
      if (useCurrentLocation) {
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject)
        );
        coords.push([pos.coords.longitude, pos.coords.latitude]);
      }

      for (const pc of postcodes) {
        const coord = await getCoordinates(pc);
        coords.push(coord);
      }

      if (coords.length < 2) throw new Error('Need at least 2 points.');

      const coordString = coords.map((c) => c.join(',')).join(';');
      const res = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/driving/${coordString}?geometries=geojson&overview=full&access_token=${mapboxgl.accessToken}`
      );
      const data = await res.json();

      const route = data.routes[0];
      const geo = route.geometry;
      const waypoints = data.waypoints.map((w) => w.location); // ← snapped marker points

      const distanceKm = (route.distance / 1000).toFixed(2);
      const durationMin = (route.duration / 60).toFixed(2);
      setInfoText(`Stops: ${coords.length} | Distance: ${distanceKm} km | Duration: ${durationMin} min`);

      mapRef.current.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: geo,
        },
      });

      mapRef.current.addLayer({
        id: 'route',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#ff5733',
          'line-width': 5,
        },
      });

      const bounds = new mapboxgl.LngLatBounds();
      waypoints.forEach((lngLat, i) => {
        const el = document.createElement('div');
        el.className = 'number-marker';
        el.textContent = i + 1;
        const marker = new mapboxgl.Marker({ element: el }).setLngLat(lngLat).addTo(mapRef.current);
        markersRef.current.push(marker);
        bounds.extend(lngLat);
      });

      mapRef.current.fitBounds(bounds, { padding: 60 });
    } catch (err) {
      console.error(err);
      setInfoText(`Error: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!mapRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [-0.1, 51.5],
        zoom: 6,
      });

      mapRef.current.on('load', () => {
        plotRoute();
      });
    }

    return () => {
      mapRef.current?.remove();
    };
  }, []);

  return (
    <>
      <div style={{ padding: '10px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          value={routeInput}
          onChange={(e) => setRouteInput(e.target.value)}
          style={{ width: '400px', padding: '6px' }}
        />
        <button onClick={plotRoute} disabled={isLoading}>
          {isLoading ? 'Loading...' : 'Plot Route'}
        </button>
        <label>
          <input
            type="checkbox"
            checked={useCurrentLocation}
            onChange={(e) => setUseCurrentLocation(e.target.checked)}
          />{' '}
          Use my current location
        </label>
        <span style={{ color: infoText.startsWith('Error') ? 'red' : 'green', fontWeight: 'bold' }}>
          {infoText}
        </span>
      </div>
      <div ref={mapContainerRef} style={{ height: '90vh' }} />

      <style>{`
        .number-marker {
          background-color: #1976d2;
          color: white;
          border-radius: 50%;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 14px;
          border: 2px solid white;
          box-shadow: 0 0 3px rgba(0,0,0,0.4);
        }
      `}</style>
    </>
  );
};

export default MapRoute;
